import {
  compact,
  flatMap,
  flatten,
  groupBy,
  isEmpty,
  isEqual,
  uniqWith,
} from 'lodash';

import {
  HoldingAvailability,
  HoldingNote,
  HoldingsStatement,
} from '@app/components';

type HoldingsStatementsKeys = Extract<
  keyof HoldingAvailability,
  | 'holdingsStatements'
  | 'holdingsStatementsForSupplements'
  | 'holdingsStatementsForIndexes'
>;

export interface HoldingsStatementWithLocation extends HoldingsStatement {
  locations: string[];
  notes?: HoldingNote[];
}

export interface GroupedUniqueStatements {
  holdingsStatements: HoldingsStatementWithLocation[];
  holdingsStatementsForSupplements: HoldingsStatementWithLocation[];
  holdingsStatementsForIndexes: HoldingsStatementWithLocation[];
  notes?: HoldingNote[];
}

export type GroupedByStatementKeysUniqueStatements = Record<
  HoldingsStatementsKeys,
  Record<string, HoldingsStatementWithLocation[]>
>;
export type GroupedByLocationUniqueStatements = Record<
  string,
  GroupedUniqueStatements
>;

const mergeUniqueItems = <T>(array: T[][]) =>
  compact(uniqWith(array.flat(), isEqual));

export const getGroupedByLocationHoldingStatements = (
  availabilityData: HoldingAvailability[]
): GroupedByLocationUniqueStatements => {
  const initialValue: GroupedByStatementKeysUniqueStatements = {
    holdingsStatements: {},
    holdingsStatementsForSupplements: {},
    holdingsStatementsForIndexes: {},
  };
  const statementsKeys = Object.keys(initialValue) as HoldingsStatementsKeys[];

  const uniqueStatementsGroupedByStatementKeys: GroupedByStatementKeysUniqueStatements =
    availabilityData.reduce((groupedStatements, currentItem) => {
      statementsKeys.forEach(holdingStatementsKey => {
        const currentItemHoldingStatement = isEmpty(
          currentItem[holdingStatementsKey]
        )
          ? [{}]
          : currentItem[holdingStatementsKey];

        const currentItemHoldingsWithLocation: HoldingsStatementWithLocation[] =
          currentItemHoldingStatement.map((item: HoldingsStatement) => ({
            ...item,
            locations: [currentItem.location.name],
            notes: currentItem.notes,
          }));

        const uniqueStatementsAndNotes = uniqWith(
          flatten([
            ...Object.values(groupedStatements[holdingStatementsKey]),
            ...currentItemHoldingsWithLocation,
          ]),
          isEqual
        ).filter(statement => !isEmpty(statement));

        groupedStatements[holdingStatementsKey] = groupBy(
          flatMap(
            groupBy(
              uniqueStatementsAndNotes,
              ({ statement, note = '', notes = [] }) =>
                statement + note + JSON.stringify(notes)
            ),
            group => ({
              statement: group[0].statement,
              ...(group[0].note && { note: group[0].note }),
              locations: mergeUniqueItems(group.map(item => item.locations)),
              notes: mergeUniqueItems(group.map(item => item.notes)),
            })
          ),
          ({ locations }) => locations.join(', ')
        );
      });

      return groupedStatements;
    }, initialValue);

  const statementsGroupedByLocation = {};

  Object.entries(uniqueStatementsGroupedByStatementKeys).forEach(
    ([statementKey, groupedLocations]) => {
      Object.entries(groupedLocations).forEach(([locationName, statements]) => {
        const statementData = statements.filter(
          ({ statement, note }) => statement || note
        );
        const group = {
          ...statementsGroupedByLocation[locationName],
          notes: statements[0]?.notes,
          ...(!isEmpty(statementData) && { [statementKey]: statementData }),
        };

        const isGroupWithData = !isEmpty(Object.values(group).flat());

        if (isGroupWithData) {
          statementsGroupedByLocation[locationName] = group;
        }
      });
    }
  );

  return statementsGroupedByLocation;
};
